import type { ChangeEvent, ReactNode } from 'react';
import { InputGroup, cn } from '@heroui/react';
import { CellSlider } from '@heroui-pro/react';
import { ColorPickerField } from '@/components/custom';
import { IconBlur, IconLetterX, IconLetterY, IconSunHigh } from '@tabler/icons-react';
import type { AppearanceShadowValue } from '@/components/canvas/shared/utils/geometry/appearanceShadow';

const inputGroupNoFocus =
  'has-[[data-slot=input-group-control]:focus-visible]:ring-0 has-[[data-slot=input-group-control]:focus-visible]:ring-offset-0 has-[[data-slot=input-group-control]:focus-visible]:shadow-none';

const inputNoFocus =
  'focus-visible:border-transparent focus-visible:ring-0 focus-visible:shadow-none outline-none focus:outline-none';

const shadowInputGroupClass = cn('min-w-0 rounded-lg border border-divider', inputGroupNoFocus);

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function clampOpacity(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function ShadowFieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[3rem_1fr] items-start gap-x-3 gap-y-1">
      <span className="pt-2 text-xs font-normal text-muted">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function ShadowNumberInput({
  ariaLabel,
  prefix,
  value,
  onChange,
}: {
  ariaLabel: string;
  prefix: ReactNode;
  value: number;
  onChange: (next: number) => void;
}) {
  return (
    <InputGroup className={shadowInputGroupClass}>
      <InputGroup.Prefix className="text-muted">{prefix}</InputGroup.Prefix>
      <InputGroup.Input
        aria-label={ariaLabel}
        type="number"
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChange(Math.max(0, Math.round(num(e.target.value))))
        }
        className={cn('w-full text-right', inputNoFocus)}
      />
    </InputGroup>
  );
}

export type ShadowSettingsPopoverProps = {
  value: AppearanceShadowValue;
  onChange: (patch: Partial<AppearanceShadowValue>) => void;
  opacityStep?: number;
};

/** 阴影详细参数 Popover 内容 */
export function ShadowSettingsPopover({
  value,
  onChange,
  opacityStep = 0.1,
}: ShadowSettingsPopoverProps) {
  return (
    <div className="w-72 space-y-3 p-1">
      <p className="text-sm font-semibold text-default-foreground">阴影设置</p>

      <ShadowFieldRow label="位置">
        <div className="space-y-2">
          <ShadowNumberInput
            ariaLabel="阴影水平偏移（像素）"
            prefix={<IconLetterX className="size-4 shrink-0" stroke={1.5} aria-hidden />}
            value={value.offsetX}
            onChange={(offsetX) => onChange({ offsetX })}
          />
          <ShadowNumberInput
            ariaLabel="阴影垂直偏移（像素）"
            prefix={<IconLetterY className="size-4 shrink-0" stroke={1.5} aria-hidden />}
            value={value.offsetY}
            onChange={(offsetY) => onChange({ offsetY })}
          />
        </div>
      </ShadowFieldRow>

      <ShadowFieldRow label="模糊">
        <ShadowNumberInput
          ariaLabel="阴影模糊（像素）"
          prefix={<IconBlur className="size-4 shrink-0" stroke={1.5} aria-hidden />}
          value={value.blur}
          onChange={(blur) => onChange({ blur })}
        />
      </ShadowFieldRow>

      <ShadowFieldRow label="扩展">
        <ShadowNumberInput
          ariaLabel="阴影扩展（像素）"
          prefix={<IconSunHigh className="size-4 shrink-0" stroke={1.5} aria-hidden />}
          value={value.spread}
          onChange={(spread) => onChange({ spread })}
        />
      </ShadowFieldRow>

      <ShadowFieldRow label="颜色">
        <ColorPickerField
          aria-label="阴影颜色"
          value={value.colorHex}
          onChange={(colorHex) => onChange({ colorHex })}
          variant="secondary"
        />
      </ShadowFieldRow>

      <ShadowFieldRow label="透明度">
        <CellSlider
          aria-label="阴影颜色透明度"
          variant="secondary"
          className="w-full"
          value={value.colorOpacity}
          minValue={0}
          maxValue={1}
          step={opacityStep}
          onChange={(v) => {
            const next = Array.isArray(v) ? v[0] : v;
            if (next == null || Number.isNaN(next)) return;
            onChange({ colorOpacity: clampOpacity(next) });
          }}
        >
          <CellSlider.Track>
            <CellSlider.Output />
            <CellSlider.Fill />
            <CellSlider.Thumb />
          </CellSlider.Track>
        </CellSlider>
      </ShadowFieldRow>
    </div>
  );
}
